import React, { useState, useMemo } from 'react';
import PropTypes from 'prop-types';

import { RideGlInformationSolid, RideGrid } from '@rimac-seguros/ride-system-components';

import PlanItem from '../../../components/molecules/plan-item-full';
import ModalDetailPlan from '../modal-detail-plan';
import { useAppContext } from '../../../AppProvider';
import { detailPlanModel } from '../../../models/step-plans';
import CardShimmer from '../../../components/atoms/card-shimmer';
import { useQuote } from '../../../hooks/useQuote';
import { useHistory } from '../../../hooks/useHistory';
import ModalClinics from '../../../components/organisms/modal-clinics/ModalClinics';
import {
  registerLead as registerLeadService,
  validatePreExisting as validatePreExistingService,
  validateSinister as validateSinisterService,
} from '../../../services/clientServices';
import { getErrorPage } from '../../../utils/helpers';
import { getTagActionName, getTagMetadata, getTagVirtual } from '../../../utils/analytics';
import ModalPortal from '../../../components/molecules/rbrModal/ModalPortal';
import Notification from '../../../assets/step-coverages/icon_notification.svg';

import './styles.scss';

const SectionPlans = ({ authoring }) => {
  const [selectPlan, setSelectPlan] = useState(null);
  const [showModalDetail, setShowModalDetail] = useState(false);
  const [showModalClinics, setShowModalClinics] = useState(false);
  const [showModalLoading, setShowModalLoading] = useState(false);

  const {
    title, description, disclaimer, plans,
  } = authoring;
  const {
    state: {
      leadData,
      client,
    },
    dispatch,
  } = useAppContext();
  const { currentPlans, loading } = useQuote(plans);
  const { navigate } = useHistory();
  const requestValidate = {
    afiliados: [
      { tipDocumento: client.tipoDocumento.toString(), numDocumento: client.numeroDocumento },
    ],
  };

  const getTitle = useMemo(() => (client.nombres ? title.replace('{name}', client.nombres.toLowerCase()) : '¡Hola!'), [client]);

  const handleSelectPlan = (plan) => {
    setShowModalLoading(true);
    registerLead(plan);
  };

  const savePlanSelected = (plan) => {
    dispatch({ type: 'SAVE_PLAN_SELECTED', payload: plan });
    setSelectPlan(null);
    navigate('/comprar/seguro-salud-flexible/asegurados');
  };

  const registerLead = async (plan) => {
    try {
      const requestLead = {
        ...leadData,
        finalizado: 2,
        atributos: {
          ...leadData.atributos,
          dscPlan: plan.name,
        },
      };
      await registerLeadService(requestLead);
      dispatch({ type: 'ADD_LEAD_DATA', payload: requestLead });
      validatePreExisting(plan);
    } catch (error) {
      navigate(getErrorPage(error, 'AGREGAR_LEADS'));
      setShowModalLoading(false);
    }
  };

  const validatePreExisting = async (plan) => {
    try {
      const { payload } = await validatePreExistingService(requestValidate);
      const { result } = payload.payload;
      validateSinister(plan, result);
    } catch (error) {
      navigate(getErrorPage(error, 'EVALUAR_PREEXISTENCIAS'));
      setShowModalLoading(false);
    }
  };

  const validateSinister = async (plan, dataPreexistence) => {
    try {
      const { payload } = await validateSinisterService(requestValidate);
      const { result } = payload.payload;

      dataPreexistence.forEach((_, i) => {
        if (_.indPreexistenciasEps === 1 || result[i].indSiniestro === 1) {
          return navigate('/comprar/seguro-salud-flexible/planes/gracias');
        }
      });
      savePlanSelected(plan);
    } catch (error) {
      navigate(getErrorPage(error, 'VALIDAR_SINIESTROS'));
    } finally {
      setShowModalLoading(false);
    }
  };

  const handleShowMore = (plan) => {
    setSelectPlan(plan);
    setShowModalDetail(true);
    getTagActionName('Seguro Salud Flexible', 'Interacciones', 'Planes - Conoce más', plan.name, 'trackAction');
    getTagVirtual('sol:comprar:seguro-salud-flexible:modal-coberturas');
  };

  return (
    <RideGrid className="section-plans-group">
      <div className="section-plans-group_head">
        <h2>{getTitle}</h2>
        <p>{description}</p>
      </div>
      {!loading && currentPlans.map((_, i) => (
        <div key={`plan-${i}`} className={`section-plans-group_plan ${_.isRecommend ? 'recommend' : ''} ${i === 0 ? 'first' : ''}`}>
          <PlanItem
            plan={_}
            onSelect={() => {
              handleSelectPlan(_);
              getTagMetadata('Plan Seguro Salud Flexible', `${_.name} - ${_.priceDiscount || _.price}`, 'trackMetadata');
            }}
            onShowMore={() => handleShowMore(_)}
            onShowClinics={() => {
              setSelectPlan(_);
              setShowModalClinics(true);
              getTagVirtual('sol:comprar:seguro-salud-flexible:modal-lista-clinicas', 'trackVirtual');
            }}
          />
        </div>
      ))}
      {loading && Array.from({ length: 4 }).map((_, i) => (
        <div key={`plan-skeleton-${i}`} className={`section-plans-group_plan-skeleton ${i === 0 ? 'first-card' : ''}`}>
          <CardShimmer />
        </div>
      ))}
      <div className="section-plans-group_disclaimer">
        <RideGlInformationSolid size="large" />
        <p>{disclaimer}</p>
      </div>
      {showModalDetail && (
        <ModalDetailPlan
          open={showModalDetail}
          defaultSelect={selectPlan?.name}
          authoring={detailPlanModel}
          onClose={() => {
            setShowModalDetail(false);
            setSelectPlan(null);
          }}
          onConfirm={() => {
            handleSelectPlan(selectPlan);
            getTagActionName('Seguro Salud Flexible', 'Interacciones', 'Modal Coberturas', `Obtenlo ahora - ${selectPlan.name}`, 'trackAction');
          }}
        />
      )}
      {showModalClinics && (
        <ModalClinics
          btnText="¡Lo quiero!"
          onClose={() => setShowModalClinics(false)}
          btnAction={() => handleSelectPlan(selectPlan)}
        />
      )}

      <ModalPortal
        isOpen={showModalLoading}
        srcIcon={Notification}
        title="¡Danos un momento!"
        description="Estamos actualizando el monto de tu plan"
        typeButton="loading"
        showIconClose={false}
      />
    </RideGrid>
  );
};

SectionPlans.propTypes = {
  authoring: PropTypes.shape({
    title: PropTypes.string.isRequired,
    description: PropTypes.string.isRequired,
    disclaimer: PropTypes.string.isRequired,
    plans: PropTypes.arrayOf(PropTypes.shape({
      nroPlanRS: PropTypes.number.isRequired,
      name: PropTypes.string.isRequired,
      price: PropTypes.number.isRequired,
      currency: PropTypes.string.isRequired,
      period: PropTypes.string.isRequired,
      description: PropTypes.string.isRequired,
      showClinics: PropTypes.bool.isRequired,
      isRecommend: PropTypes.bool.isRequired,
      coverages: PropTypes.arrayOf(PropTypes.shape({
        name: PropTypes.string.isRequired,
        description: PropTypes.string.isRequired,
      })).isRequired,
      benefits: PropTypes.arrayOf(PropTypes.shape({
        name: PropTypes.string.isRequired,
        description: PropTypes.string.isRequired,
      })).isRequired,
    })).isRequired,
  }).isRequired,
};

export default SectionPlans;
